/* eslint-disable react/display-name */
import React, { Fragment, Suspense } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import AuthGuard from './components/Auth/AuthGuard';
import AuthGuardExternal from './components/Auth/AuthRoles';
import DashboardLayout from './components/Navigation';
import LoadingScreen from './components/Loading/LoadingScreen';

import Login from './views/Login';
import Verification from './views/Verification';
import PasswordRecovery from './views/PasswordRecovery';
import Home from './views/Home';

import UserListView from './views/Admin/Users/UserListView';
import UserCreateView from './views/Admin/Users/UserCreateView';
import UserEditView from './views/Admin/Users/UserEditView';

import ProductGraphs from './views/Products/ProductGraphs';
import ProductListView from './views/Products/ProductList';
import ProductEditView from './views/Products/ProductEdit';

import EngelProductConfigView from './views/EngelProducts/EngelProductConfig';
import EngelProductEditView from './views/EngelProducts/EngelProductEdit';
import EngelProductCreateView from './views/EngelProducts/EngelProductCreate';
import EngelProductGraphs from './views/EngelProducts/EngelProductGraphs';

import AveragePriceGraphs from './views/ProductSnapshots/AveragePriceGraph';
import ProductsnapshotListView from './views/ProductSnapshots/SnapsListView';
import ProductCounts from './views/ProductCounts';

import CategoriesListView from './views/Categories/CategoriesListView';
import CategoryEditView from './views/Categories/CategoryEdit';
import CategoryCreateView from './views/Categories/CategoryCreate';

import SubcategoriesListView from './views/Subcategories/SubcategoriesListView';
import InactiveSubcategoriesListView from './views/Subcategories/InactiveSubcategoriesListView';
import SubcategoryEditView from './views/Subcategories/SubcategoryEdit';
import SubcategoryCreateView from './views/Subcategories/SubcategoryCreate';

import ScraperStatistics from './views/Scraper/ScraperStatistics';
import SendEmailView from './views/PasswordRecovery/SendEmail';

import ReportListView from './views/Reports/ReportList';
import ReportCreateView from './views/Reports/ReportCreate';
import ReportEditView from './views/Reports/ReportEdit';
import AlarmsView from './views/Alarms';
import SupermarketsListView from './views/Supermarkets/SupermarketsListView';

const routesConfig = [
  {
    id: 1,
    exact: true,
    path: '/',
    component: () => <Redirect to="/inicio" />,
  },
  {
    id: 2,
    exact: true,
    path: '/ingreso',
    component: () => <Login />,
  },
  {
    id: 3,
    exact: true,
    path: '/user-verification/:token',
    component: () => <Verification />,
  },
  {
    id: 4,
    exact: true,
    path: '/reset-password/:token',
    component: () => <PasswordRecovery />,
  },
  {
    id: 5,
    exact: true,
    path: '/reset-password?token=:token',
    component: () => <Redirect to="/reset-password/:token" />,
  },
  {
    id: 6,
    exact: true,
    path: '/recover-password',
    component: () => <SendEmailView />,
  },
  {
    path: '/inicio',
    // AuthGuard (not AuthGuardExternal): this is the default landing page
    // for every user, external users included.
    guard: AuthGuard,
    layout: DashboardLayout,
    routes: [
      {
        id: 8,
        exact: true,
        path: '/inicio',
        component: () => <Home />,
      },
    ],
  },
  {
    path: '/administracion',
    guard: AuthGuardExternal,
    layout: DashboardLayout,
    routes: [
      {
        id: 11,
        exact: true,
        path: '/administracion',
        component: () => <Redirect to="/administracion/users" />,
      },
      {
        id: 12,
        exact: true,
        path: '/administracion/users',
        component: () => <UserListView />,
      },
      {
        id: 13,
        exact: true,
        path: '/administracion/users/crear',
        component: () => <UserCreateView />,
      },
      {
        id: 14,
        exact: true,
        path: '/administracion/users/:id/editar',
        component: () => <UserEditView />,
      },
      {
        id: 70,
        exact: true,
        path: '/administracion/reportes',
        component: () => <ReportListView />,
      },
      {
        id: 71,
        exact: true,
        path: '/administracion/reportes/crear',
        component: () => <ReportCreateView />,
      },
      {
        id: 74,
        exact: true,
        path: '/administracion/reportes/:id/editar',
        component: () => <ReportEditView />,
      },
      {
        id: 15,
        component: () => <Redirect to="/administracion/users" />,
      },
    ],
  },
  {
    path: '/datos',
    guard: AuthGuard,
    layout: DashboardLayout,
    routes: [
      {
        id: 16,
        exact: true,
        path: '/datos/productsnapshots',
        component: () => <ProductsnapshotListView />,
      },
      {
        id: 17,
        exact: true,
        path: '/datos/products/:id/graphs',
        component: () => <ProductGraphs />,
      },
      {
        id: 18,
        exact: true,
        path: '/datos/products/:id/editar',
        component: () => <ProductEditView />,
      },
      {
        id: 20,
        exact: true,
        path: '/datos/productos',
        component: () => <ProductListView />,
      },
      {
        id: 75,
        exact: true,
        path: '/datos/conteo-productos',
        component: () => <ProductCounts />,
      },
    ],
  },
  {
    path: '/graphs',
    guard: AuthGuardExternal,
    layout: DashboardLayout,
    routes: [
      {
        id: 19,
        exact: true,
        path: '/graphs/avg-price',
        component: () => <AveragePriceGraphs />,
      },
    ],
  },
  {
    path: '/config',
    guard: AuthGuard,
    layout: DashboardLayout,
    routes: [
      {
        id: 20.5,
        exact: true,
        path: '/config/supermarkets',
        component: () => <SupermarketsListView />,
      },
      {
        id: 21,
        exact: true,
        path: '/config/categories',
        component: () => <CategoriesListView />,
      },
      {
        id: 22,
        exact: true,
        path: '/config/categories/crear',
        component: () => <CategoryCreateView />,
      },
      {
        id: 23,
        exact: true,
        path: '/config/category/:id/edit',
        component: () => <CategoryEditView />,
      },
      {
        id: 24,
        exact: true,
        path: '/config/subcategories',
        component: () => <SubcategoriesListView />,
      },
      {
        id: 24.5,
        exact: true,
        path: '/config/subcategories/inactive',
        component: () => <InactiveSubcategoriesListView />,
      },
      {
        id: 25,
        exact: true,
        path: '/config/subcategories/crear',
        component: () => <SubcategoryCreateView />,
      },
      {
        id: 26,
        exact: true,
        path: '/config/subcategory/:id/edit',
        component: () => <SubcategoryEditView />,
      },
      {
        id: 27,
        exact: true,
        path: '/config/alarms',
        component: () => <AlarmsView />,
      },
    ],
  },
  {
    path: '/engel',
    guard: AuthGuardExternal,
    layout: DashboardLayout,
    routes: [
      {
        id: 27,
        exact: true,
        path: '/engel/engel-products/',
        component: () => <EngelProductConfigView />,
      },
      {
        id: 28,
        exact: true,
        path: '/engel/engel-products/:id/edit',
        component: () => <EngelProductEditView />,
      },
      {
        id: 29,
        exact: true,
        path: '/engel/engel-products/crear',
        component: () => <EngelProductCreateView />,
      },
      {
        id: 30,
        exact: true,
        path: '/engel/engel-products/:id/graphs',
        component: () => <EngelProductGraphs />,
      },
      {
        id: 31,
        exact: true,
        path: '/engel/scraper',
        component: () => <ScraperStatistics />,
      },
    ],
  },
];

const renderRoutes = (routes) => (routes ? (
  <Suspense fallback={<LoadingScreen />}>
    <Switch>
      {routes.map((route, i) => {
        const Guard = route.guard || Fragment;
        const Layout = route.layout || Fragment;
        const Component = route.component;

        return (
          <Route
            key={i}
            path={route.path}
            exact={route.exact}
            render={(props) => (
              <Guard>
                <Layout>
                  {route.routes ? (
                    renderRoutes(route.routes)
                  ) : (
                    <Component {...props} />
                  )}
                </Layout>
              </Guard>
            )}
          />
        );
      })}
    </Switch>
  </Suspense>
) : null);

function Routes() {
  return renderRoutes(routesConfig);
}

export default Routes;
